import React, { useState } from "react";
import { IArticle } from "../../core/redux/dataMap/article";
import {
  Paper,
  ArticlePaperContainer,
  ArticlePaperHeader,
  ArticlePaperSnippets,
  PaperTitle,
  ArticlePaperActionButtons,
  PathText,
  SubPaper,
} from "./style/styledComponents";
import { ButtonsList } from "../../components/ButtonsList";
import { FeedbackStatus } from "./FeedbackStatus";
import { Droppable, Draggable } from "react-beautiful-dnd";
import {
  useTheme,
  Grid,
  Typography,
  makeStyles,
  createStyles,
  Theme,
  IconButton,
  SvgIcon,
  TextField,
} from "@material-ui/core";
import { ArticlePaperSnippet } from "./ArticlePaperSnippet";
import { ArticlePaperFooter } from "./ArticlePaperFooter";
import { ISnippet } from "../../core/redux/dataMap/snippet";
import { ContentClipper } from "./ContentClipper";
import { Actions } from "../../core/redux/ducks/article";
import { ReactComponent as EditorIcon } from "../../assets/imgs/icons/editor.svg";
import SearchIcon from "@material-ui/icons/Search";
import { Creators } from "../../core/redux/ducks/article";
import { useDispatch, useSelector } from "react-redux";
import useLanguage from "../../hooks/useLanguage";
import ExtensionButton from "../../components/ExtensionButton";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import EditIcon from "@material-ui/icons/Edit";
interface IArticlePaperContentProps {
  article: IArticle;
  onHide(): void;
  deleteSnippet(snippet: ISnippet, articleId: number | null): void;
  saveSnippet(): void;
  frameSource: { url: string };
  status: string;
  error: string;
  activeTab: string;
  setActiveTab(tab: string): void;
  openTagModal(): void;
}

export const ArticlePaperContent: React.FC<IArticlePaperContentProps> = ({
  article,
  onHide,
  deleteSnippet,
  frameSource,
  saveSnippet,
  status,
  error,
  activeTab,
  setActiveTab,
  openTagModal,
}) => {
  const [isEditMode, setEditMode] = useState(false);
  const theme = useTheme();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { language } = useLanguage();
  const { translate } = language;
  const { user } = useSelector((state) => state.user);

  const setLabel = (snippetId, label) =>
    Actions.setSnippetLabel(dispatch, article.id, snippetId, label);

  const removeLabel = (snippetId, label) =>
    Actions.removeSnippetLabel(dispatch, article.id, snippetId, label);

  const editArticle = (article) => Actions.updateArticle(dispatch, article);

  const exportToWord = () => {
    Actions.exportToWord(article.id);
  };

  const handleEditArticleName = (e) => {
    if (e.key === "Enter") {
      editArticle({ ...article, name: e.target.value });
      setEditMode(false);
    }
  };

  const resetArticle = () => dispatch(Creators.resetArticle());
  const optionsButtons = article.id
    ? [
        {
          onClick: () => openTagModal(),
          icon: "tag",
        },
        {
          onClick: () => exportToWord(),
          icon: "file-word-o",
        },

        {
          onClick: () => onHide(),
          icon: "close",
        },
      ]
    : [
        {
          onClick: () => onHide(),
          icon: "close",
        },
      ];

  return (
    <ArticlePaperContainer theme={theme}>
      <ArticlePaperHeader container theme={theme}>
        <ArticlePaperActionButtons item md={12} lg={12} theme={theme}>
          <Grid container justify="space-between">
            <Grid item>
              <FeedbackStatus
                status={status}
                error={error}
                statusType="editor"
              />
            </Grid>

            <Grid item>
              <ButtonsList noPadding buttons={optionsButtons} />
            </Grid>
          </Grid>
        </ArticlePaperActionButtons>
      </ArticlePaperHeader>
      <Paper theme={theme}>
        {article.id ? (
          <Grid container className={classes.paperContainer}>
            {!!user.isPremium && (
              <Grid item md={1}>
                <Grid container direction="column" spacing={2}>
                  <Grid
                    item
                    className={
                      activeTab === "editor"
                        ? classes.activeTab
                        : classes.tabButton
                    }
                  >
                    <IconButton
                      onClick={() => setActiveTab("editor")}
                      className={classes.IconButtonTab}
                      color={activeTab === "editor" ? "primary" : "default"}
                    >
                      <SvgIcon>
                        <EditorIcon width={25} height={25} />
                      </SvgIcon>
                    </IconButton>
                  </Grid>
                  <Grid
                    item
                    className={
                      activeTab === "search"
                        ? classes.activeTab
                        : classes.tabButton
                    }
                  >
                    <IconButton
                      onClick={() => setActiveTab("search")}
                      className={classes.IconButtonTab}
                      color={activeTab === "search" ? "primary" : "default"}
                    >
                      <SearchIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid item md={user.isPremium ? 11 : 12}>
              <SubPaper theme={theme}>
                <Grid container>
                  <Grid
                    item
                    md={user.isPremium ? 8 : 12}
                    lg={user.isPremium ? 10 : 12}
                  >
                    <PathText>{article.path}</PathText>
                    <PaperTitle item lg={12} theme={theme}>
                      {!isEditMode ? (
                        article.name
                      ) : (
                        <TextField
                          placeholder="Article name"
                          defaultValue={article.name}
                          onKeyPress={handleEditArticleName}
                        />
                      )}
                    </PaperTitle>
                  </Grid>
                  {!!user.isPremium && (
                    <Grid item md={2} lg={1}>
                      <IconButton
                        onClick={() => setEditMode(true)}
                        className={classes.IconButtonTab}
                      >
                        <EditIcon />
                      </IconButton>
                    </Grid>
                  )}
                  {!!user.isPremium && (
                    <Grid item md={2} lg={1}>
                      <IconButton
                        onClick={resetArticle}
                        className={classes.IconButtonTab}
                      >
                        <ArrowForwardIcon />
                      </IconButton>
                    </Grid>
                  )}
                </Grid>
                <Droppable
                  droppableId={"column-article"}
                  key={"column-article"}
                >
                  {(provided) => (
                    <ArticlePaperSnippets
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {article && article.snippets.length ? (
                        article.snippets.map((snippet, index) => (
                          <Draggable
                            key={index}
                            draggableId={`column-article-${index}`}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <ArticlePaperSnippet
                                  article={article}
                                  snippet={snippet}
                                  handleDelete={() =>
                                    deleteSnippet(snippet, article.id)
                                  }
                                  isDragging={snapshot.isDragging}
                                  isEditor={false}
                                  setLabel={setLabel}
                                  removeLabel={removeLabel}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))
                      ) : (
                        <>
                          <Typography
                            variant="caption"
                            display="block"
                            gutterBottom
                            className={classes.defaultSnipet}
                          >
                            {translate.empty_snippet}
                          </Typography>
                          <ExtensionButton />
                        </>
                      )}
                      {provided.placeholder}
                    </ArticlePaperSnippets>
                  )}
                </Droppable>
                <ArticlePaperFooter
                  saveSnippet={saveSnippet}
                  articleID={article.id}
                  source={frameSource}
                />
              </SubPaper>
            </Grid>
          </Grid>
        ) : (
          <ContentClipper />
        )}
      </Paper>
    </ArticlePaperContainer>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paperContainer: {
      height: "100%",
    },
    buttonBorder: {
      borderTopLeftRadius: "10px",
    },
    activeTab: {
      backgroundImage: "url('images/rect_bg_icon.PNG')",
      backgroundRepeat: "no-repeat",
      textAlign: "left",
      backgroundColor: "white",
      marginLeft: "-3px",
      height: "45px",
      width: "33px",
      padding: "8px 0 !important",
      color: theme.palette.primary.main,
    },
    IconButtonTab: {
      padding: "5px",
    },
    tabButton: {
      padding: "9px 0 !important",
    },
    defaultSnipet: {
      marginTop: "10px",
    },
  })
);
